import {
	createContext,
	type FC,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import {
	clearWipColumns,
	countWipVisibleColumns,
	DEFAULT_WIP_COLUMN_SELECTION,
	isWipColumnChecked,
	isWipColumnVisible,
	selectAllWipColumns,
	setWipComparisonView,
	toggleWipColumn,
	type WipColumnSelection,
} from "./wipColumnSelection";
import type { WipAnalysisGridMetricId } from "./wipMetric";

type WipColumnSelectorContextValue = {
	toggleMetric: (metricId: WipAnalysisGridMetricId) => void;
	selectAllMetrics: () => void;
	clearAllMetrics: () => void;
	setComparisonView: (enabled: boolean) => void;
	comparisonView: boolean;
	isWipColumnVisible: (metricId: WipAnalysisGridMetricId) => boolean;
	isWipColumnChecked: (metricId: WipAnalysisGridMetricId) => boolean;
	visibleCount: number;
};

const WipColumnSelectorContext = createContext<WipColumnSelectorContextValue | null>(null);

export const useWipColumnSelector = (): WipColumnSelectorContextValue => {
	const ctx = useContext(WipColumnSelectorContext);
	if (!ctx) {
		throw new Error("useWipColumnSelector must be used inside WipColumnSelectorProvider");
	}
	return ctx;
};

type WipColumnSelectorProviderProps = {
	children: ReactNode;
};

export const WipColumnSelectorProvider: FC<WipColumnSelectorProviderProps> = ({ children }) => {
	const [selection, setSelection] = useState<WipColumnSelection>(DEFAULT_WIP_COLUMN_SELECTION);

	const toggleMetric = useCallback(
		(metricId: WipAnalysisGridMetricId) => setSelection((prev) => toggleWipColumn(prev, metricId)),
		[],
	);

	const selectAllMetrics = useCallback(() => setSelection(selectAllWipColumns), []);

	const clearAllMetrics = useCallback(() => setSelection(clearWipColumns), []);

	const setComparisonView = useCallback(
		(enabled: boolean) => setSelection((prev) => setWipComparisonView(prev, enabled)),
		[],
	);

	const isColumnVisible = useCallback(
		(metricId: WipAnalysisGridMetricId) => isWipColumnVisible(selection, metricId),
		[selection],
	);

	const isColumnChecked = useCallback(
		(metricId: WipAnalysisGridMetricId) => isWipColumnChecked(selection, metricId),
		[selection],
	);

	const visibleCount = useMemo(() => countWipVisibleColumns(selection), [selection]);

	const value = useMemo(
		() => ({
			toggleMetric,
			selectAllMetrics,
			clearAllMetrics,
			setComparisonView,
			comparisonView: selection.comparisonView,
			isWipColumnVisible: isColumnVisible,
			isWipColumnChecked: isColumnChecked,
			visibleCount,
		}),
		[
			toggleMetric,
			selectAllMetrics,
			clearAllMetrics,
			setComparisonView,
			selection.comparisonView,
			isColumnVisible,
			isColumnChecked,
			visibleCount,
		],
	);

	return <WipColumnSelectorContext value={value}>{children}</WipColumnSelectorContext>;
};
